import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { useDispatch } from 'react-redux';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useCatalogs } from 'hooks';

import type {
  Client,
  ClientInfo,
  ClientDetails,
  LegalRepresentative,
  BankerContract,
  ClientDetailData,
} from 'api/client/models';
import {
  getClientById,
  getClientsFilter,
  getClientStatus,
  updateClientContractBanker,
} from 'api/client';

import { getUsersByRoleAndName } from 'api/user/user-filter';

import { cleanData } from 'store/clients';

import Customer, {
  CustomerType,
  ValueCustumer,
} from 'context/customer-data/context';
import UserRoleContext from 'context/user-role/context';
import CurrentCustomer from 'context/global-customer-creation/context';
import {
  CurrentCustomerType,
  DataCurrentCustomerType,
} from 'context/global-customer-creation/types';

import { QUERY_STALE_TIME_DEFAULT } from 'constants/globals';

import type { Pagination } from 'commons/modules/pagination';
import type { GenericResponse } from 'commons/modules/generic-response';

import ClientsManagement from './context';

import type {
  Props,
  ClientsManagementType,
  ClientDetailParamsType,
  LegalRepresentativeType,
  FormModalType,
} from './types';
import {
  fetchAllClientsByBlocks,
  updateExistingClientInArray,
  upsertClientInArray,
} from './helpers';

const initPagination = {
  page: 1,
  pageSize: 50,
  total: 0,
  isLastPage: false,
};

const ClientsManagementProvider = ({ children }: Props) => {
  const queryClient = useQueryClient();
  const { setCustomer, updateCustomer, getClientDetailData } = useContext(
    Customer,
  ) as CustomerType;
  const { role } = useContext(UserRoleContext);
  const { updateCurrentCustomer } = useContext(
    CurrentCustomer,
  ) as CurrentCustomerType;

  const { pathname } = useLocation();
  const catalog = useCatalogs();
  const dispatch = useDispatch();

  const [dataOriginal, setDataOriginal] = useState<Client[]>([]);

  // From table context
  const [refreshData, setRefreshData] = useState(false);

  const [pagination, setPagination] = useState<Pagination>({
    ...initPagination,
  });
  const [data, setData] = useState<Client[]>([]);
  // End from table context

  const [formModalOpen, setFormModalOpen] = useState<FormModalType>({
    isOpen: false,
    contractId: null,
    client: {},
  });
  const [clientStatus, setClientStatus] = useState<ClientDetails | null>(null);
  const [clientData, setClientData] = useState<
    ClientDetailData | undefined | null
  >(null);
  const [matchClients, setMatchClients] = useState<ClientInfo[]>([]);
  const [matchLegalRepresentatives, setMatchLegalRepresentatives] = useState<
    LegalRepresentativeType[]
  >([]);
  const [clientName, setClientName] = useState('');

  const {
    data: clientsData,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ['clients'],
    queryFn: () => fetchAllClientsByBlocks(),
    staleTime: QUERY_STALE_TIME_DEFAULT,
    enabled: String(pathname).includes('clientes'),
  });

  const colspan = 1;
  const pageNum = pagination.page - 1;

  const onChangePage = (event: any, value: number) => {
    const totalPages = Math.ceil((pagination.total || 0) / pagination.pageSize);
    const newPage = value + 1;
    const isLastPage = newPage === totalPages;
    setPagination({
      page: newPage,
      pageSize: pagination.pageSize,
      isLastPage,
    });
  };

  const onChangePageSize = (event: React.ChangeEvent<any>) => {
    setPagination({
      page: 1,
      pageSize: +event.target.value,
      total: pagination.total,
      isLastPage: false,
    });
  };

  const getClientStatusData = async (clientId: number, account: string) => {
    const response = await getClientStatus({
      clientId,
      account,
    });

    const { success, data: statusData } = response;

    if (success) setClientStatus(statusData);
  };

  const handleRefreshData = () => {
    setRefreshData(true);
  };

  const handleRefreshClientById = async (
    clientId: number,
  ): Promise<boolean> => {
    if (!clientId) return false;
    try {
      const resp = await getClientById(clientId);
      if (!resp.success || !resp.data?.data) return false;

      const updatedClient = resp.data.data;

      setDataOriginal((prev) => upsertClientInArray(prev, updatedClient));
      setData((prev) => updateExistingClientInArray(prev, updatedClient));
      queryClient.setQueryData(['clients'], (old: Client[] = []) =>
        upsertClientInArray(old, updatedClient),
      );

      queryClient.invalidateQueries({ queryKey: ['clientDetail', clientId] });

      return true;
    } catch (err) {
      console.error('Error refreshing client by id:', err);
      return false;
    }
  };

  const getClientData = async (
    {
      clientId,
      clientRole,
      taxPersonType,
      contractId,
      fullName,
    }: ValueCustumer,
    account: string | null,
  ) => {
    const { success, data: dataResponse } = await updateCustomer({
      profile: null,
      clientId,
      contractId,
      fullName,
      clientRole,
      taxPersonType,
    });

    if (success) setClientData(dataResponse);

    const { success: statusSuccess, data: statusData } = await getClientStatus({
      clientId,
      account,
    });

    if (statusSuccess) setClientStatus(statusData);

    return success && statusSuccess;
  };

  const processClientDetailData = async (
    { clientId, taxPersonType }: ClientDetailParamsType,
    account: string,
  ) => {
    const respClientDetail = await getClientDetailData({
      clientId,
      taxPersonType,
    });

    const respClientAccounts = await getClientStatus({
      clientId,
      account,
    });

    return {
      respClientDetail,
      respClientAccounts,
    };
  };

  const processUpdateCurrentCustomer = (
    respClientDetail: GenericResponse<ClientDetailData>,
    respClientAccounts: GenericResponse<ClientDetails>,
    { clientId, contractId, fullName, clientRole, taxPersonType }: Client,
  ) => {
    if (!respClientDetail || !respClientAccounts) return false;

    if (respClientDetail.success) {
      setCustomer(respClientDetail.data);
      updateCurrentCustomer({
        profile: null,
        clientId,
        contractId,
        fullName,
        onboardingComplete: true,
        clientRole,
        onboardingStep: undefined,
        profileDescription: undefined,
        taxPersonType,
      } as DataCurrentCustomerType);
      setClientData(respClientDetail.data);
    }

    if (respClientAccounts.success) {
      setClientStatus(respClientAccounts.data);
    }

    return respClientDetail.success && respClientAccounts.success;
  };

  const getFilter = async (name: string, userRole: string) => {
    if (name) {
      const { data: dataResponse, success } = await getClientsFilter({
        role: userRole,
        name,
        filterBy: 'NAME',
        amib: null,
      });
      if (success && dataResponse) {
        const clients = dataResponse.data.map((client) => {
          return { ...client, fullName: String(client.fullName).toUpperCase() };
        });
        setMatchClients(clients);
        return;
      }
    }
    setMatchClients([]);
  };

  const getLegalRepresentativeFilter = async (
    legalRepresentativeName: string,
  ) => {
    const { data: dataResponse, success } = await getUsersByRoleAndName({
      names: legalRepresentativeName,
    });
    if (success && dataResponse && dataResponse.data) {
      const clients = dataResponse.data.map((client) => {
        const {
          name,
          fathersLastName,
          mothersLastName,
          amibRegistration,
          email,
        } = client;
        return {
          fullName:
            `${name} ${fathersLastName} ${mothersLastName}`.toUpperCase(),
          amibRegistration,
          email,
        };
      });
      setMatchLegalRepresentatives(clients);
    }
    setMatchClients([]);
  };

  const resetPagination = () => {
    setPagination({ ...initPagination });
  };

  const assignLegalRepresentative = async (
    selectedLegalRepresentative: LegalRepresentative,
  ) => {
    const requestBody: BankerContract = {
      amibRegistration: selectedLegalRepresentative.amibRegistration,
      bankerName: selectedLegalRepresentative.fullName,
      operationType: 'ADD',
      clients: [
        {
          contractId: formModalOpen.contractId || '',
        },
      ],
    };
    const response = await updateClientContractBanker(requestBody);
    if (response.success) {
      setClientStatus(null);
    }
    return { status: response.status };
  };

  useEffect(() => {
    setDataOriginal(clientsData || []);
  }, [clientsData]);

  useEffect(() => {
    let isMounted = true;

    if (
      isMounted &&
      refreshData &&
      (pathname === '/clientes' || pathname === '/clientes/')
    ) {
      refetch();
      queryClient.invalidateQueries({
        queryKey: ['clientDetail'],
      });
      setRefreshData(false);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, refreshData]);

  useEffect(() => {
    return () => {
      dispatch(cleanData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: ClientsManagementType = {
    assignLegalRepresentative,
    catalog,
    clientData,
    clientName,
    clientStatus: clientStatus || ({} as ClientDetails),
    colspan,
    data,
    dataOriginal,
    error: isError ? (error as Error)?.message : undefined,
    formModalOpen,
    getClientData,
    getClientStatusData,
    processClientDetailData,
    processUpdateCurrentCustomer,
    getLegalRepresentativeFilter,
    getFilter,
    loading: isLoading || isFetching,
    matchClients,
    matchLegalRepresentatives,
    onChangePage,
    onChangePageSize,
    pageNum,
    pagination,
    role,
    setClientData,
    setClientName,
    setClientStatus,
    setData,
    setFormModalOpen,
    setMatchClients,
    setMatchLegalRepresentatives,
    handleRefreshData,
    handleRefreshClientById,
    resetPagination,
  };
  return (
    <ClientsManagement.Provider value={value}>
      {children}
    </ClientsManagement.Provider>
  );
};
export default ClientsManagementProvider;
